import { ZodError } from 'zod'
import {
  ERROR_CODES,
  GenericException,
  GenericExceptionError,
} from '@reward-platform/shared-api/exceptions'

interface ContentfulError extends Error {
  stack?: string
  status: number
  statusText: string
  message: string
  details?: {
    errors?: Array<{
      name: string
      value: string
    }>
  }
}

export class ContentfulException extends GenericException {
  constructor(error?: GenericExceptionError) {
    super({
      ...ERROR_CODES.CONTENTFUL_ERROR,
      ...(error || {}),
      originalError: error?.originalError,
    })

    if (this.originalError instanceof ZodError) {
      this.validationErrors = this.originalError.issues
    }

    if (this.originalError) {
      const contentfulError = ContentfulException.normalizeContentfulError(this.originalError)
      this.stack = contentfulError?.stack
      this.originalError = contentfulError
    }
  }

  private static isContentfulError(error: Error) {
    try {
      const errorBody = JSON.parse(error.message) as ContentfulError
      return (
        errorBody &&
        typeof errorBody === 'object' &&
        'status' in errorBody &&
        'statusText' in errorBody &&
        'message' in errorBody &&
        typeof errorBody.status === 'number' &&
        typeof errorBody.statusText === 'string' &&
        typeof errorBody.message === 'string'
      )
    } catch {
      return false
    }
  }

  private static normalizeContentfulError(error: Error): GenericExceptionError['originalError'] {
    const defaultError = {
      name: 'ContentfulError',
      message: error.message,
      stack: error.stack,
    }
    if (!ContentfulException.isContentfulError(error)) {
      return defaultError
    }

    const errorBody = JSON.parse(error.message) as ContentfulError

    return {
      name: defaultError.name,
      message: errorBody.message,
      code: errorBody.details?.errors?.[0]?.value,
      status: errorBody.status,
      statusText: errorBody.statusText,
      stack: new Error(errorBody.message).stack,
    }
  }
}
